import { useEffect, type FC } from 'react';
import type { ITracker, TrackerConfig } from './types';
import type { TAnalyticsItem } from '../types';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

// Convert TAnalyticsItem to FB content format
const toFBContent = (item: TAnalyticsItem) => ({
  id: item.item_id,
  quantity: item.quantity || 1,
  item_price: item.price,
});

// Get currency from item or fall back to channel default
const getCurrency = (item: TAnalyticsItem, fallback: string) => item.currency || fallback;
const getCurrencyFromItems = (items: TAnalyticsItem[], fallback: string) =>
  items[0]?.currency || fallback;

export const createFacebookTracker = (pixelId: string, config: TrackerConfig): ITracker => {
  const { channel, debug } = config;
  const defaultCurrency = channel.defaultCurrency;

  const fbq = (...args: unknown[]) => {
    if (typeof window === 'undefined' || !window.fbq) {
      if (debug) {
        console.warn('[FB Pixel] window.fbq not available');
      }
      return;
    }
    window.fbq(...args);
    if (debug) {
      console.log('[FB Pixel]', ...args);
    }
  };

  return {
    init() {
      if (typeof window === 'undefined') return;
      if (debug) {
        console.log('[FB Pixel] Initialized with', pixelId);
      }
    },

    trackPageView({ url }) {
      fbq('track', 'PageView');
    },

    identify({ email, data }) {
      // FB Pixel uses init with user data for advanced matching
      fbq('init', pixelId, { em: email, ...data });
    },

    trackViewItem({ item }) {
      fbq('track', 'ViewContent', {
        content_ids: [item.item_id],
        content_type: 'product',
        content_name: item.item_name,
        content_category: item.item_category,
        value: item.price,
        currency: getCurrency(item, defaultCurrency),
      });
    },

    trackViewItemList({ items, listId, listName }) {
      fbq('track', 'ViewContent', {
        content_ids: items.map((item) => item.item_id),
        content_type: 'product_group',
        content_name: listName,
        currency: getCurrencyFromItems(items, defaultCurrency),
      });
    },

    trackAddToCart({ item, quantity, value }) {
      fbq('track', 'AddToCart', {
        content_ids: [item.item_id],
        content_type: 'product',
        content_name: item.item_name,
        value,
        currency: getCurrency(item, defaultCurrency),
        contents: [toFBContent({ ...item, quantity })],
      });
    },

    trackRemoveFromCart({ item, quantity, value }) {
      fbq('trackCustom', 'RemoveFromCart', {
        content_ids: [item.item_id],
        content_type: 'product',
        value,
        currency: getCurrency(item, defaultCurrency),
      });
    },

    trackViewCart({ items, value }) {
      fbq('trackCustom', 'ViewCart', {
        content_ids: items.map((item) => item.item_id),
        content_type: 'product',
        value,
        currency: getCurrencyFromItems(items, defaultCurrency),
        num_items: items.length,
      });
    },

    trackBeginCheckout({ items, value }) {
      fbq('track', 'InitiateCheckout', {
        content_ids: items.map((item) => item.item_id),
        content_type: 'product',
        value,
        currency: getCurrencyFromItems(items, defaultCurrency),
        num_items: items.length,
        contents: items.map(toFBContent),
      });
    },

    trackAddShippingInfo({ items, value, shippingTier }) {
      fbq('trackCustom', 'AddShippingInfo', {
        content_ids: items.map((item) => item.item_id),
        value,
        currency: getCurrencyFromItems(items, defaultCurrency),
        shipping_tier: shippingTier,
      });
    },

    trackAddPaymentInfo({ items, value, paymentType }) {
      fbq('track', 'AddPaymentInfo', {
        content_ids: items.map((item) => item.item_id),
        value,
        currency: getCurrencyFromItems(items, defaultCurrency),
        payment_type: paymentType,
      });
    },

    trackPurchase({ transactionId, items, value, tax, shipping }) {
      fbq('track', 'Purchase', {
        content_ids: items.map((item) => item.item_id),
        content_type: 'product',
        value,
        currency: getCurrencyFromItems(items, defaultCurrency),
        num_items: items.length,
        contents: items.map(toFBContent),
      });
    },

    trackAddToWaitlist({ email, item, value }) {
      fbq('trackCustom', 'AddToWaitlist', {
        content_ids: [item.item_id],
        content_name: item.item_name,
        value,
        currency: getCurrency(item, defaultCurrency),
        email,
      });
    },

    trackEvent({ event, data }) {
      fbq('trackCustom', event, data);
    },
  };
};

interface FacebookScriptProps {
  pixelId: string;
}

export const FacebookScript: FC<FacebookScriptProps> = ({ pixelId }) => {
  useEffect(() => {
    if (window.fbq) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    const fbq = function (...args: unknown[]) {
      fbq.callMethod ? fbq.callMethod.apply(fbq, args) : fbq.queue.push(args);
    } as ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue: unknown[];
      push: unknown;
      loaded: boolean;
      version: string;
    };
    fbq.queue = [];
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    window.fbq = fbq;
    w._fbq = fbq;

    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
  }, [pixelId]);

  return null;
};
